import {
	createContext,
	FC,
	ReactNode,
	useContext,
	useEffect,
	useState,
	useRef,
	useMemo,
	useCallback,
	memo,
} from 'react';

import { useOpenTokContext } from '@/contexts/useOpenTok';
import { getUserDataFromStream } from '@/helpers/user';
import without from 'lodash/without';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { audioStreamsState } from '@/recoil/atom/audioStreamState';
import SignalProvider from '@/contexts/useSignal';
import { toast } from 'react-toastify';
import useTranslation from 'next-translate/useTranslation';
import {
	connectedStreamsState,
	connectedStudentsState,
	instructorStreamsState,
	screenStreamsState,
	streamsState,
} from '@/recoil/atom/streamState';
import { connectionsSelector } from '@/recoil/atom/userStatus';
import { filteredInstructorIds } from '@/recoil/atom/instructorState';
import useUserStatusManager from '@/hooks/useUserStatusManager';
import useCreateClassUserSession from '@/hooks/useCreateClassUserSession';
import useConnectedTo from '@/hooks/useSetConnectedTo';
import usePinningManager from '@/hooks/usePinningManager';
import {
	appStatusAtom,
	connectionIdAtom,
	isSessionConnectedAtom,
} from '@/recoil/atom/globalState';
import { customBugsnagLogger } from '@/utils/bugsnag';
import { userConnectionState } from '@/recoil/atom/userConnectionState';
import { events, severity } from '@/config/log_events';
import NoSessionScreen from '@/components/noSessionScreen';
import VideoPinSync from '@/components/VideoPinSync';
import { useLoggingContext } from './useLogging';

type Props = {
	sessionToken: string;
	sessionId: string;
	classSlug: string;
	viewerId: string;
	children: ReactNode;
};

type ContextType = {
	isReconnecting: boolean;
	getSession: () => OT.Session | null;
	reconnectSession: () => void;
	sessionId: string;
};

const Context = createContext<ContextType>(undefined as unknown as ContextType);

const SessionProvider: FC<Props> = ({
	children,
	sessionToken,
	viewerId,
	classSlug,
	sessionId,
}: Props) => {
	const Opentok = useOpenTokContext();
	const { logToLambda: log } = useLoggingContext();
	const { setStatus } = useUserStatusManager();
	const { setConnected } = useConnectedTo();
	const setConnectedRef = useRef(setConnected);
	const { createUserSession, otConnectionId } = useCreateClassUserSession();

	const setStreamsState = useSetRecoilState(streamsState);
	const setScreenStreams = useSetRecoilState(screenStreamsState);
	const setUserConnectionStatus = useSetRecoilState(userConnectionState);
	const [connectedStreams, setConnectedStreams] = useRecoilState(
		connectedStreamsState
	);
	const setAudioStreamsState = useSetRecoilState(audioStreamsState);
	const setInstructorStreams = useSetRecoilState(instructorStreamsState);
	const setConnectedStudent = useSetRecoilState(connectedStudentsState);
	const setAppStatus = useSetRecoilState(appStatusAtom);
	const userConnections = useRecoilValue(connectionsSelector);
	const invisibleGhostIds = useRecoilValue(filteredInstructorIds('invisible'));

	const { t } = useTranslation('default');
	const [session, setSession] = useState<OT.Session | null>(null);

	const [isConnected, setIsConnected] = useRecoilState(isSessionConnectedAtom);
	const [isReconnecting, setIsReconnecting] = useState(false);
	const [isConnecting, setIsConnecting] = useState(false);
	const setConnectionIdAtom = useSetRecoilState(connectionIdAtom);
	const [reconnectTrigger, setReconnectTrigger] = useState(0);

	const shouldCancel = useRef(false);
	const {
		handleSetBoardPinned,
		delayedBoardUnpin,
		checkBoardPinned,
		showVideoFeed,
		toggleVideoPin,
	} = usePinningManager();
	const sessionRef = useRef<OT.Session>();
	const activeConnectionsRef = useRef<string[] | undefined>();

	const getSession = useCallback(() => session, [session]);

	const reconnectSession = useCallback(() => {
		customBugsnagLogger.debug('reconnectSession invoked');
		try {
			sessionRef.current?.disconnect();
		} catch (e) {
			customBugsnagLogger.debug(
				`reconnectSession: disconnect threw: ${(e as Error)?.message ?? e}`
			);
		}
		shouldCancel.current = false;
		setIsConnected(false);
		setIsReconnecting(false);
		setIsConnecting(false);
		setSession(null);
		sessionRef.current = undefined;
		// Reset session-scoped state so the re-connect starts from a clean slate.
		setStreamsState({});
		setScreenStreams({});
		setInstructorStreams([]);
		setAudioStreamsState([]);
		setUserConnectionStatus([]);
		// Clear connectionIdAtom so useCreateClassUserSession will issue a new
		// session record on the fresh connection (otherwise the effect's
		// `otConnectionId` guard blocks the re-connect).
		setConnectionIdAtom(false as unknown as string);
		// Bumping the trigger forces the connect effect to re-evaluate.
		setReconnectTrigger((n) => n + 1);
	}, [
		setIsConnected,
		setStreamsState,
		setScreenStreams,
		setInstructorStreams,
		setAudioStreamsState,
		setUserConnectionStatus,
		setConnectionIdAtom,
	]);

	useEffect(() => {
		const boardPinnedJson = localStorage.getItem('boardPinnedUsers');
		if (boardPinnedJson) {
			const parsed = JSON.parse(boardPinnedJson);
			handleSetBoardPinned(parsed);
			setTimeout(() => {
				checkBoardPinned();
			}, 10000);
		}
	}, []);

	useEffect(() => {
		// Ghost / super-ghost connections are part of the haunting feature and must
		// stay invisible to the haunted user — they must not surface as the
		// viewer's own active connections (which would, e.g., block them from
		// joining a workspace). Connection records carry the User account role, not
		// the class-session role, so we identify ghosts by id via the invisible
		// instructor selector (which keys off the session role).
		const visibleConnections = userConnections?.filter(
			(connection: { id: any }) => !invisibleGhostIds.includes(connection.id)
		);
		const ids = visibleConnections?.map((connection: { id: any }) => connection.id);
		const student = visibleConnections?.find(
			(connection: { role: any }) => connection.role === 'student'
		);

		if (student) {
			setConnectedStudent(student.id);
			toggleVideoPin(student.id, true);
		} else {
			setConnectedStudent(undefined);
		}

		// Connecting to a user auto-pins their video feed and clears any force-hide
		ids?.forEach((id: string) => {
			showVideoFeed(id);

		});

		setConnectedStreams(ids || []);
	}, [setConnectedStreams, setConnectedStudent, userConnections, invisibleGhostIds]);

	useEffect(() => {
		activeConnectionsRef.current = connectedStreams;
	}, [connectedStreams]);

	useEffect(() => {
		setConnectedRef.current = setConnected;
	}, [setConnected]);

	useEffect(() => {
		if (isConnected || isConnecting || shouldCancel.current || otConnectionId)
			return;
		console.log('%c Session provider starting! ', 'background: #1f9e11;');
		console.log(`%c ${sessionId} `, 'background: #1f9e11;');
		setIsConnecting(true);
		const updateSessionAppStatus = (status: number) => {
			setAppStatus((prevStatus) => ({
				...prevStatus,
				sessionStatus: status,
			}));
		};

		const updateConnectionAppStatus = (status: number) => {
			setAppStatus((prevStatus) => ({
				...prevStatus,
				connectionStatus: status,
			}));
		};

		const sessionDisconnected = ({ reason }: { reason: string }) => {
			customBugsnagLogger.debug(`Session disconnected ${reason}`);
			console.log('%c Session disconnected', 'background: #f00;');
			log(events.OT_SESSION_DISCONNECTED, {
				classSlug,
				metadata: { reason, sessionId, status: 'offline' },
			});
			// Legacy toast — superseded by ConnectionHealthBanner (useConnectionHealth).
			// Commented for review; remove once banner UX is validated in QA.
			// if (reason === 'networkDisconnected') {
			// 	toast(t('connection.session_disconnected'), {
			// 		type: 'error',
			// 	});
			// }
			updateSessionAppStatus(2);
			setStatus(null, 'offline');
			shouldCancel.current = false;
			setIsConnected(false);
			setIsReconnecting(false);
			setSession(null);
			sessionRef.current = undefined;
			setStreamsState({});
		};

		const sessionReconnecting = () => {
			updateSessionAppStatus(0);
			customBugsnagLogger.debug('Session reconnecting');
			log(events.OT_SESSION_RECONNECTING, {
				classSlug,
				metadata: { sessionId },
			});
			console.log(t('connection.reconnecting'));
			setIsReconnecting(true);
		};

		const sessionReconnected = () => {
			updateSessionAppStatus(1);
			customBugsnagLogger.debug('Session reconnected');
			log(events.OT_SESSION_RECONNECTED, {
				classSlug,
				metadata: { sessionId },
			});
			setIsReconnecting(false);
		};

		const streamCreated = ({ stream }: { stream: OT.Stream }) => {
			const data = getUserDataFromStream(stream);

			if (data.id === viewerId) {
				log(events.DUPLICATE_SESSION, {
					user: { id: viewerId },
					classSlug,
					metadata: { sessionId },
				});
			}
			if (stream.videoType === 'screen') {
				setScreenStreams((prevStreams) => ({
					...prevStreams,
					[data.id]: stream,
				}));
			} else {
				if (data.role === 'instructor') {
					setInstructorStreams((newStreams) => [...newStreams, data.id]);
				}
				if (stream?.hasAudio) {
					setAudioStreamsState((ids) =>
						ids.includes(data.id) ? ids : [...ids, data.id]
					);
				} else {
					setAudioStreamsState((ids) => without(ids, data.id));
				}
				setStreamsState((prevStreams) => ({
					...prevStreams,
					[data.id]: stream,
				}));
			}
		};

		const streamDestroyed = ({
			stream,
			reason,
		}: {
			stream: OT.Stream;
			reason: string;
		}) => {
			const data = getUserDataFromStream(stream);

			if (stream.videoType === 'screen') {
				setScreenStreams(({ [data.id]: _, ...newStreams }) => ({
					...newStreams,
				}));
			} else {
				delayedBoardUnpin(data.id);

				if (data.role === 'instructor') {
					setInstructorStreams((instructors) => without(instructors, data.id));
				}
				setStreamsState(({ [data.id]: _, ...newStreams }) => ({
					...newStreams,
				}));
				console.log('stream destroyed');
				customBugsnagLogger.debug(`Subscriber stream destroyed ${reason}`);
			}
		};

		const streamPropertyChanged = ({
			changedProperty,
			newValue,
			stream,
		}: {
			changedProperty: 'hasAudio' | 'hasVideo';
			newValue: boolean;
			stream: OT.Stream;
		}) => {
			const data = getUserDataFromStream(stream);

			if (changedProperty === 'hasAudio') {
				if (newValue) {
					return setAudioStreamsState((ids) => [...ids, data.id]);
				}
				return setAudioStreamsState((ids) => without(ids, data.id));
			}
		};

		const sessionConnected = () => {
			console.log('session connected');
			updateSessionAppStatus(1);
			customBugsnagLogger.debug('Session connected handler');
			log(events.OT_SESSION_CREATED, {
				classSlug,
				metadata: { sessionId },
			});
		};

		// OT reports every connection in the session, and one account can hold more
		// than one at a time: a second device, or — far more common — the outgoing
		// page during the full-page lobby→class redirect, which OT only reaps a
		// moment later. Identity has to be the connectionId. Keying off data.id
		// (the userId) makes a live tab treat another connection's teardown as its
		// own and write itself offline, and makes the instructor tear down a
		// student who is still in the room.
		// Falls back to "not mine" when session.connection isn't populated yet:
		// sessionDisconnected already covers a genuine local disconnect, so the
		// safe direction is to not claim a connection we can't prove is ours.
		const isOwnConnection = (connection: OT.Connection) =>
			!!session.connection &&
			connection.connectionId === session.connection.connectionId;

		const connectionCreated = ({
			connection,
		}: {
			connection: OT.Connection;
		}) => {
			console.log('Connection created');
			const data = JSON.parse(connection.data);
			if (isOwnConnection(connection)) {
				console.log('current user connection created');
				setIsConnecting(false);
				setIsConnected(true);
				setIsReconnecting(false);
				createUserSession(sessionId, connection?.connectionId);
				updateConnectionAppStatus(1);
				log(events.OT_CONNECTION_CREATED, {
					metadata: { connection: connection?.connectionId, data },
				});
			} else if (data.id === viewerId) {
				// Another connection of this same account (second device, or the
				// page we just navigated away from). Not a peer: it must not join
				// the peer list, and it must not be mistaken for our own.
				customBugsnagLogger.debug(
					`other connection for current user (${viewerId}) created: ${connection?.connectionId}`
				);
			} else {
				console.log('%c other user connection created', 'background: #f00;');
				log(events.OT_OTHER_CONNECTION_CREATED, {
					metadata: { data, connection: connection?.connectionId },
				});
				setUserConnectionStatus((prevConnections) => [
					...prevConnections,
					data.id,
				]);
			}
		};

		const connectionDestroyed = ({
			connection,
			reason,
		}: {
			connection: OT.Connection;
			reason: string;
		}) => {
			const data = JSON.parse(connection.data);
			if (isOwnConnection(connection)) {
				customBugsnagLogger.debug(
					`current user (${viewerId}) connection disconnected`
				);
				shouldCancel.current = false;
				setIsConnected(false);
				updateConnectionAppStatus(2);
				// Legacy toast — superseded by ConnectionHealthBanner (useConnectionHealth).
				// Commented for review; remove once banner UX is validated in QA.
				// toast(t('connection.session_disconnected'), {
				// 	type: 'error',
				// });
				log(events.OT_CONNECTION_DESTROYED, {
					severity: severity.ERROR,
					metadata: { reason },
				});
				setStatus(null, 'offline');
			} else if (data.id === viewerId) {
				// One of our own other connections went away. Reacting here is what
				// made a live tab mark itself offline (and its connections torn
				// down) when a stale one was reaped. Nothing to do.
				customBugsnagLogger.debug(
					`other connection for current user (${viewerId}) destroyed: ${connection?.connectionId} reason: ${reason}`
				);
			} else {
				setUserConnectionStatus((prevConnections) =>
					without(prevConnections, data.id)
				);
				if (activeConnectionsRef.current?.includes(data.id)) {
					customBugsnagLogger.debug(
						`connected user disconnected ${data.id} reason: ${reason}`
					);
					console.log('connected user disconnected');
					toast(`${data.firstName} ${t('status.disconnected')}`, {
						type: 'warning',
					});
					setStatus(data.id, 'offline');
					setConnectedRef.current(data.id, false);
				}
			}
		};

		// eslint-disable-next-line no-shadow
		const session = Opentok.initSession(process.env.VONAGE_API_KEY ?? '', sessionId);

		const sessionHandlers = {
			sessionDisconnected,
			sessionReconnecting,
			sessionReconnected,
			sessionConnected,
			connectionCreated,
			connectionDestroyed,
			streamCreated,
			streamDestroyed,
			streamPropertyChanged,
		};

		session.on(sessionHandlers);

		session.connect(sessionToken, (error) => {
			if (error) {
				console.log(error);
				shouldCancel.current = false;
				setIsConnecting(false);
				setIsConnected(false);
				setIsReconnecting(false);
				setSession(null);
				sessionRef.current = undefined;
				setStreamsState({});
				customBugsnagLogger.error(error);
				return;
			}
			shouldCancel.current = true;
			setStatus(null, 'online');
			setSession(session);
			setStreamsState({});
			customBugsnagLogger.debug('Session connected');
		});
		shouldCancel.current = true;
		customBugsnagLogger.debug('Setting session');
		setSession(session);
		sessionRef.current = session;

		return () => {
			if (!isConnected) return;
			session.off(sessionHandlers);
			session.disconnect();
			shouldCancel.current = false;
			setIsConnected(false);
			setIsReconnecting(false);
			setSession(null);
			sessionRef.current = undefined;
			setStreamsState({});
			setStatus(null, 'offline');
			log(events.OT_SESSION_DESTROYED, {
				metadata: { isCleanup: true, sessionId },
			});
			customBugsnagLogger.debug('Session cleanup');
		};
	}, [
		sessionToken,
		isConnected,
		sessionId,
		Opentok,
		isConnecting,
		setStatus,
		createUserSession,
		classSlug,
		viewerId,
		t,
		setAudioStreamsState,
		setStreamsState,
		setScreenStreams,
		setInstructorStreams,
		delayedBoardUnpin,
		setIsConnected,
		setUserConnectionStatus,
		setAppStatus,
		otConnectionId,
		log,
		reconnectTrigger,
	]);

	// MAT-1075: guaranteed A/V teardown on unmount. The connect effect's own
	// cleanup is neutralized once connected — its top guard early-returns, so it
	// never re-registers a live cleanup, and the only cleanup that was registered
	// captured `isConnected === false` and therefore no-ops. As a result
	// `session.disconnect()` never runs on logout (which soft-navigates to
	// /dashboard, unmounting this provider), leaving a zombie OpenTok connection.
	// This dedicated unmount-only effect disconnects via the ref, for regular
	// participants and ghosts alike. `off()` first so the post-disconnect handlers
	// don't fire setState on the unmounted provider.
	useEffect(
		() => () => {
			const activeSession = sessionRef.current;
			if (!activeSession) return;
			try {
				activeSession.off();
				activeSession.disconnect();
				log(events.OT_SESSION_DESTROYED, {
					metadata: { isUnmount: true, sessionId },
				});
				customBugsnagLogger.debug('Session disconnected on unmount');
			} catch (e) {
				customBugsnagLogger.debug(
					`Unmount disconnect threw: ${(e as Error)?.message ?? e}`
				);
			} finally {
				// `off()` above suppresses the `sessionDisconnected` handler that
				// normally resets these. They're app-global Recoil atoms that survive
				// the soft-navigation away from the class, so without an explicit
				// reset the next SessionProvider mount sees stale `isConnected` /
				// `connectionId` and its connect guard early-returns — the user is
				// stuck on NoSessionScreen until a hard refresh. Mirror
				// reconnectSession's teardown so re-entry starts clean.
				setIsConnected(false);
				setConnectionIdAtom(false as unknown as string);
				setStreamsState({});
				setScreenStreams({});
				setInstructorStreams([]);
				setAudioStreamsState([]);
				setUserConnectionStatus([]);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const value = useMemo(
		() => ({
			isReconnecting,
			reconnectSession,
			sessionId,
		}),
		[isReconnecting, reconnectSession, sessionId]
	);

	return session ? (
		<Context.Provider
			// eslint-disable-next-line react/jsx-no-constructed-context-values
			value={{ ...value, getSession }}
		>
			<SignalProvider>
				<VideoPinSync />
				{children}
			</SignalProvider>
		</Context.Provider>
	) : (
		<NoSessionScreen />
	);
};

export const useSessionContext = (): ContextType => useContext(Context);

export default memo(SessionProvider);
